import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowUpRight } from 'lucide-react';
import { useCMS } from '../context/CMSContext';
import { industries as defaultIndustries } from '../data/mockData';

const FADE_UP = { opacity: 0, y: 20 };
const VISIBLE = { opacity: 1, y: 0 };

const IndustryCard = ({ item, index, inView, span }) => (
  <motion.a
    href="#contact"
    initial={{ opacity: 0, y: 30 }}
    animate={inView ? VISIBLE : {}}
    transition={{ delay: 0.07 * index, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative overflow-hidden rounded-2xl border border-white/5 hover:border-white/20 transition-all duration-500 ${span}`}
    data-testid={`industry-card-${item.id}`}
  >
    <div className="relative aspect-[4/5] lg:aspect-auto lg:h-full lg:min-h-[420px]">
      <img
        src={item.image}
        alt={`${item.name} digital solutions by Digitize`}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050914] via-[#050914]/60 to-transparent" />
      <div className="absolute inset-0 bg-[#050914]/20 group-hover:bg-[#050914]/0 transition-colors duration-500" />
      <div className="absolute inset-0 p-7 lg:p-8 flex flex-col justify-end">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs tracking-[0.2em] uppercase text-white/60 font-bold">
            Industry · {String(index + 1).padStart(2, '0')}
          </span>
        </div>
        <h3 className="font-display text-3xl lg:text-4xl font-medium tracking-tight text-white leading-tight">
          {item.name}
        </h3>
        <p className="mt-3 text-white/65 text-sm lg:text-base font-light leading-relaxed max-w-md">
          {item.blurb}
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-white/80 text-sm font-medium">
          <span className="border-b border-white/30 group-hover:border-white pb-0.5 transition-colors">Explore work</span>
          <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
        </div>
      </div>
    </div>
  </motion.a>
);

const IndustriesSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const { get } = useCMS();
  const items = get('industries') || defaultIndustries;

  // Bento spans: first card large, others normal
  const spans = [
    'lg:col-span-2 lg:row-span-2',
    'lg:col-span-1',
    'lg:col-span-1',
    'lg:col-span-1',
    'lg:col-span-1',
  ];

  return (
    <section id="industries" ref={ref} className="relative py-24 lg:py-32 bg-[#050914] border-t border-white/5" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-20">
          <div className="max-w-2xl">
            <motion.p initial={FADE_UP} animate={inView ? VISIBLE : {}}
              className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
              <span className="w-8 h-px bg-white/30" />
              Industries We Serve
            </motion.p>
            <motion.h2 initial={FADE_UP} animate={inView ? VISIBLE : {}} transition={{ delay: 0.1 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter text-white leading-[1.05]">
              Built for the businesses that build everything else.
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2 gap-5 lg:gap-6">
          {items.slice(0, 5).map((item, index) => (
            <IndustryCard
              key={item.id}
              item={item}
              index={index}
              inView={inView}
              span={spans[index] || 'lg:col-span-1'}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
